/**
 * Chat-only code-block ergonomics.
 *
 * - Typing ``` opens a code block IMMEDIATELY on the third backtick — no
 *   Enter/space needed (Slack behavior; per Omkar, language tags are not a
 *   chat feature, so the stock ```lang fence paths are unreachable here by
 *   design — the block opens before a tag could be typed). Wiki keeps the
 *   stock fence + language rules.
 * - Enter inside a block ALWAYS inserts a newline — consecutive Enters make
 *   blank lines and never exit. The ONLY keyboard exit is ArrowDown
 *   (`exitOnArrowDown`). A double-Enter exit shipped briefly and was
 *   reverted on staging feedback: real code needs blank lines, and any
 *   Enter-count exit steals them. Don't reintroduce it.
 *
 * Plain `Extension` — contributes NO schema names, so the chat editor set in
 * `richtext-surfaces.json` stays byte-identical (the drift test enforces it).
 */
import type { Editor } from '@tiptap/react'
import { Extension, InputRule } from '@tiptap/react'

export const ChatCodeBlockBehavior = Extension.create({
  name: 'chatCodeBlockBehavior',
  addKeyboardShortcuts() {
    return {
      // Stock behavior (exit-code-or-insert-break) + an explicit reveal:
      // a TRAILING hardBreak's caret rect is measured on the OLD line, so
      // ProseMirror's own scrollIntoView no-ops and the new line hides
      // below a max-height composer's fold (staging finding — composer,
      // message edit, and thread reply all share this surface).
      'Shift-Enter': ({ editor }) => {
        const handled = editor.commands.setHardBreak()
        if (handled) {
          requestAnimationFrame(() => {
            const view = editor.view
            let el: HTMLElement | null = view.dom.parentElement
            while (el && getComputedStyle(el).overflowY !== 'auto') el = el.parentElement
            if (!el || el.scrollHeight <= el.clientHeight) return
            const head = view.state.selection.head
            if (head >= view.state.doc.content.size - 1) {
              // Caret at the very end of the doc — pin to the bottom.
              el.scrollTop = el.scrollHeight
              return
            }
            try {
              const c = view.coordsAtPos(head, -1)
              const r = el.getBoundingClientRect()
              const line = Math.max(c.bottom - c.top, 18)
              if (c.bottom + line > r.bottom) {
                el.scrollTop += c.bottom + line - r.bottom + 8
              }
            } catch {
              // coordsAtPos can throw during concurrent updates — the
              // reveal is best-effort chrome, never worth an error.
            }
          })
        }
        return handled
      },
    }
  },
  addInputRules() {
    return [
      new InputRule({
        // Permissive trigger: the third backtick just typed. The handler
        // verifies real document context — the runner's text extraction
        // renders a hardBreak as a 7-char '%leaf%' placeholder, so a
        // line-start anchor can't live in the regex (that's why ``` after
        // Shift+Enter — and therefore in message EDIT, where bare Enter
        // saves — never fired with the /^```$/ form; staging finding).
        find: /```$/,
        handler: ({ state, range, chain }) => {
          const caret = range.to
          const $caret = state.doc.resolve(caret)
          if ($caret.parent.type.name !== 'paragraph') return null
          const codeBlock = state.schema.nodes.codeBlock
          if (!codeBlock) return null
          // Only at the very end of the paragraph (mid-text ``` stays
          // literal), with exactly two in-doc backticks before the caret.
          if ($caret.parentOffset !== $caret.parent.content.size) return null
          if ($caret.parentOffset < 2) return null
          if (state.doc.textBetween(caret - 2, caret) !== '``') return null
          const $bt = state.doc.resolve(caret - 2)
          if ($bt.parentOffset === 0) {
            // ``` is the whole paragraph → convert in place.
            chain()
              .command(({ tr }) => {
                tr.delete(caret - 2, caret)
                tr.setBlockType(caret - 2, caret - 2, codeBlock)
                return true
              })
              .run()
            return
          }
          // ``` on a Shift+Enter soft line → strip the break + fence and
          // open a fresh block BELOW the paragraph (prior lines survive).
          if ($bt.nodeBefore?.type.name !== 'hardBreak') return null
          const breakPos = caret - 3
          const afterParagraph = $caret.after() - 3 // after deleting 3 positions
          chain()
            .command(({ tr }) => {
              tr.delete(breakPos, caret)
              return true
            })
            .insertContentAt(afterParagraph, { type: 'codeBlock' })
            .setTextSelection(afterParagraph + 1)
            .run()
        },
      }),
    ]
  },
})

/**
 * Compact-toolbar (chat surfaces) code-block button behavior: with a plain
 * caret in a NON-EMPTY paragraph, insert a fresh empty code block AFTER it
 * instead of converting the paragraph — a Shift+Enter multi-line paragraph
 * is ONE node, and converting it swallowed every soft-wrapped line into the
 * block (staging finding). Empty paragraph / active block / real selection
 * keep the stock toggle semantics.
 */
export function toggleOrInsertCodeBlock(editor: Editor): boolean {
  const { state } = editor
  const { $from, empty } = state.selection
  const inBlock = editor.isActive('codeBlock')
  if (
    !inBlock &&
    empty &&
    $from.parent.type.name === 'paragraph' &&
    $from.parent.content.size > 0
  ) {
    const after = $from.after()
    return editor
      .chain()
      .focus()
      .insertContentAt(after, { type: 'codeBlock' })
      .setTextSelection(after + 1)
      .run()
  }
  return editor.chain().focus().toggleCodeBlock().run()
}
